import React, { useState, useEffect } from 'react';
import { KivlopeLogo, UserIcon, PlusIcon } from './components/Icons';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, serverTimestamp, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import { encodeEmail } from './utils/helpers';

// Import UI Components
import LoadingScreen from './screens/LoadingScreen';
import InfoScreen from './screens/InfoScreen';
import LoginScreen from './screens/LoginScreen';
import SetUsernameScreen from './screens/SetUsernameScreen';
import Dashboard from './screens/Dashboard';
import FriendsScreen from './screens/FriendsScreen';
import ProfileScreen from './screens/ProfileScreen';
import CreateCapsuleModal from './modals/CreateCapsuleModal';
import AppHeader from './components/AppHeader';



// --- Main App Component ---
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [activeScreen, setActiveScreen] = useState('dashboard'); // 'dashboard', 'profile', or 'people'
  const [isCreating, setIsCreating] = useState(false);
  const [prefilledRecipient, setPrefilledRecipient] = useState('');
  const [prefilledTitle, setPrefilledTitle] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setUserProfile(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (documentSnapshot) => {
        setUserProfile(documentSnapshot.exists() ? documentSnapshot.data() : null);
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [user]);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoading(true);
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleReply = (recipient, title) => {
    setPrefilledRecipient(recipient);
    setPrefilledTitle(title);
    setIsCreating(true);
  };

  const handleSealKivlope = async (capsuleData) => {
    if (!user) return;
    try {
      const { isImmediate, title, message, resolvedEmails, media, disappearOption, disappearHours } = capsuleData;
      const status = isImmediate ? 'ready' : 'locked';
      const unlockDate = isImmediate ? new Date() : capsuleData.unlockDate;

      const recipientEmails = resolvedEmails;
      if (!recipientEmails || recipientEmails.length === 0) {
        console.error("No valid recipients found after resolution.");
        return;
      }

      const batch = writeBatch(db);
      const groupId = doc(collection(db, 'idGenerator')).id;

      const recipientStatuses = {};
      recipientEmails.forEach(email => {
        const encodedEmail = encodeEmail(email);
        recipientStatuses[encodedEmail] = { status: 'visible', trashedAt: null };
      });

      let disappearAt = null;
      if (disappearOption === 'after_time' && disappearHours > 0) {
        disappearAt = new Date(Date.now() + disappearHours * 60 * 60 * 1000);
      }

      // --- CORRECTED LOGIC ---
      const senderStatus = (disappearOption === 'on_view' || disappearOption === 'after_time')
        ? 'deleted'
        : 'visible';

      const capsulePayload = {
        title, message, senderId: user.uid,
        senderEmail: user.email.toLowerCase(), status, unlockDate,
        createdAt: serverTimestamp(), groupId, allRecipients: recipientEmails,
        media: media || [],
        senderStatus, // Use the corrected senderStatus
        senderTrashedAt: senderStatus === 'trashed' ? serverTimestamp() : null,
        recipientStatuses,
        disappearOption: disappearOption || 'none',
        disappearAt,
      };

      for (const email of recipientEmails) {
        const capsuleRef = doc(collection(db, "capsules"));
        batch.set(capsuleRef, {
          ...capsulePayload,
          recipientEmail: email,
        });
        const requestRef = doc(collection(db, "invitation_requests"));
        batch.set(requestRef, { senderId: user.uid, recipientEmail: email, senderEmail: user.email, senderName: user.displayName || user.email, capsuleTitle: title, capsuleId: capsuleRef.id, });
      }
      await batch.commit();
      setIsCreating(false);
    } catch (error) { console.error("Error sealing Kivlope: ", error); }
  };

  return (
    <div className="bg-[#121212] text-[#EAEAEA] min-h-screen font-sans">
      {(() => {
        if (loading) return <LoadingScreen />;
        if (showInfo) return <InfoScreen onBack={() => setShowInfo(false)} />;
        if (!user) return <LoginScreen onLogin={handleLogin} onInfoClick={() => setShowInfo(true)} />;

        if (userProfile === null) return <LoadingScreen message="Finalizing your profile..." />;

        if (!userProfile.username) return <SetUsernameScreen user={user} />;

        return (
          <div className="max-w-4xl mx-auto p-4 sm:p-8 relative">
            <CreateCapsuleModal
              key={isCreating}
              onSeal={handleSealKivlope}
              onClose={() => setIsCreating(false)}
              isVisible={isCreating}
              user={user}
              initialRecipient={prefilledRecipient}
              initialTitle={prefilledTitle}
            />
            <AppHeader
              user={user}
              userProfile={userProfile}
              onLogout={handleLogout}
              activeScreen={activeScreen}
              setScreen={setActiveScreen}
              onInfoClick={() => setShowInfo(true)}
            />
            {activeScreen === 'dashboard' && <Dashboard user={user} onReply={handleReply} />}
            {activeScreen === 'friends' && <FriendsScreen user={user} onComposeClick={(username) => { setPrefilledRecipient(username); setIsCreating(true); }} />}
            {activeScreen === 'profile' && <ProfileScreen user={user} setScreen={setActiveScreen} />}

            {activeScreen !== 'profile' && (
              <div className="fixed bottom-8 right-8">
                <button onClick={() => { setPrefilledRecipient(''); setPrefilledTitle(''); setIsCreating(true); }} className="bg-[#D4AF37] text-[#121212] font-bold rounded-full w-16 h-16 flex items-center justify-center shadow-lg hover:bg-[#C09A2E] transition-transform hover:scale-110">
                  <PlusIcon className="w-8 h-8" />
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}